/**
 * Sistema de Tracking para A/B Testing
 * Maneja eventos de Facebook Pixel y Google Analytics
 */

import { trackingConfig } from '../config/abTestConfig';

class TrackingManager {
    constructor() {
        this.config = trackingConfig;
        this.debug = process.env.NODE_ENV === 'development';
        this.initializeTracking();
    }

    /**
     * Inicializar sistemas de tracking
     */
    initializeTracking() {
        this.initializeFacebookPixel();
        this.initializeGoogleAnalytics();
    }

    /**
     * Inicializar Facebook Pixel si no está ya cargado
     */
    initializeFacebookPixel() {
        if (!this.config.facebook.enabled) return;

        // Verificar si Facebook Pixel ya está cargado
        if (typeof window !== 'undefined' && !window.fbq) {
            if (this.debug) {
                console.log('📊 Facebook Pixel not detected, initializing...');
            }

            // Crear función fbq si no existe
            window.fbq = window.fbq || function () {
                (window.fbq.q = window.fbq.q || []).push(arguments);
            };
            window.fbq.version = '2.0';
            window.fbq.queue = [];
        }
    }

    /**
     * Inicializar Google Analytics si no está ya cargado
     */
    initializeGoogleAnalytics() {
        if (!this.config.analytics.enabled) return;

        // Verificar si Google Analytics ya está cargado
        if (typeof window !== 'undefined' && !window.gtag) {
            if (this.debug) {
                console.log('📊 Google Analytics not detected, initializing...');
            }

            // Crear función gtag si no existe
            window.dataLayer = window.dataLayer || [];
            window.gtag = function () {
                window.dataLayer.push(arguments);
            };
            window.gtag('js', new Date());
        }
    }

    /**
     * Enviar evento a Facebook Pixel
     */
    sendFacebookEvent(eventName, parameters = {}) {
        if (!this.config.facebook.enabled) return;

        try {
            if (typeof window !== 'undefined' && window.fbq) {
                window.fbq('track', eventName, parameters);

                if (this.debug) {
                    console.log('📊 Facebook Event:', eventName, parameters);
                }
            } else if (this.debug) {
                console.log('📊 Facebook Event (not sent - no pixel):', eventName, parameters);
            }
        } catch (error) {
            console.warn('Error sending Facebook event:', error);
        }
    }

    /**
     * Enviar evento a Google Analytics
     */
    sendAnalyticsEvent(eventName, parameters = {}) {
        if (!this.config.analytics.enabled) return;

        try {
            if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', eventName, parameters);

                if (this.debug) {
                    console.log('📊 Analytics Event:', eventName, parameters);
                }
            } else if (this.debug) {
                console.log('📊 Analytics Event (not sent - no gtag):', eventName, parameters);
            }
        } catch (error) {
            console.warn('Error sending Analytics event:', error);
        }
    }

    /**
     * Trackear exposición de variante A/B
     */
    trackVariantExposure(testName, variant, additionalData = {}) {
        const baseParameters = {
            test_name: testName,
            variant: variant,
            timestamp: new Date().toISOString(),
            ...additionalData
        };

        // Google Analytics
        this.sendAnalyticsEvent(
            this.config.analytics.events.variantExposure,
            {
                event_category: 'AB_Testing',
                event_label: `${testName}_${variant}`,
                custom_parameters: baseParameters
            }
        );
    }

    /**
     * Trackear carga del sitio con variante activa
     */
    trackSiteLoad(activeVariants = {}) {
        const parameters = {
            active_variants: activeVariants,
            timestamp: new Date().toISOString(),
            page_url: window.location.href
        };
    }

    /**
     * Trackear apertura del cotizador
     */
    trackCotizadorOpen(activeVariants = {}) {
        const parameters = {
            active_variants: activeVariants,
            timestamp: new Date().toISOString(),
            source_page: window.location.href
        };

        this.sendAnalyticsEvent(this.config.analytics.events.formStart);
    }

    /**
     * Trackear paso completado en formulario
     */
    trackFormStep(step, testName, variant, additionalData = {}) {
        const parameters = {
            step: step,
            test_name: testName,
            variant: variant,
            timestamp: new Date().toISOString(),
            ...additionalData
        };

        this.sendAnalyticsEvent(
            this.config.analytics.events.formStep,
            {
                event_category: 'AB_Testing',
                event_label: `form_step_${step}`,
                custom_parameters: parameters
            }
        );
    }

    /**
     * Trackear evento personalizado
     */
    trackCustomEvent(eventName, parameters = {}) {
        this.sendAnalyticsEvent(eventName, parameters);
    }

    /**
     * Obtener todas las variantes activas
     */
    getActiveVariants(abTestingManager) {
        const activeVariants = {};

        if (abTestingManager && abTestingManager.tests) {
            for (const [testName] of abTestingManager.tests) {
                const variant = abTestingManager.getVariant(testName);
                if (variant) {
                    activeVariants[testName] = variant;
                }
            }
        }

        return activeVariants;
    }
}

// Crear instancia global
const trackingManager = new TrackingManager();

export default trackingManager;

export function getAttributionData() {
  try {
    const raw = sessionStorage.getItem("queryParams");
    const params = raw ? JSON.parse(raw) : {};
    return {
      gclid: params.gclid || null,
      fbclid: params.fbclid || null,
      ttclid: params.ttclid || null,
    };
  } catch {
    return { gclid: null, fbclid: null, ttclid: null };
  }
}